import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';
import { Badge, Button, Card, EmptyState, Input, Select, Stack, Text } from '../components/atoms';
import { Alert } from '../components/atoms/Alert/Alert';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { AdminArea } from '../components/admin/AdminArea';
import { AdminTable } from '../components/admin/AdminTable';
import { AdminConfirmDialog, type AdminConfirmRequest } from '../components/admin/AdminConfirmDialog';
import { formatDate } from '../components/admin/adminFormat';
import {
  anonymizeUser,
  exportUserData,
  fetchAdminUsers,
  fetchInactiveUsers,
} from '../services/adminService';

// Same default as the backend (gdpr.controller.ts InactiveUsersQuerySchema).
const DEFAULT_THRESHOLD_DAYS = 90;

function downloadJson(fileName: string, json: string): void {
  const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * Admin → GDPR: data export (Art. 15/20) and anonymisation (Art. 17) of one
 * user, and the retention check for inactive accounts
 * (/api/v1/admin/gdpr/users/:id/export, …/anonymize, /inactive-users).
 */
export function AdminGdprPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const users = useQuery({ queryKey: ['admin', 'users', 'all'], queryFn: () => fetchAdminUsers('all') });
  const [userId, setUserId] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);
  const [confirm, setConfirm] = useState<AdminConfirmRequest | null>(null);
  const [done, setDone] = useState('');

  const [thresholdInput, setThresholdInput] = useState(String(DEFAULT_THRESHOLD_DAYS));
  const [threshold, setThreshold] = useState(DEFAULT_THRESHOLD_DAYS);
  const [thresholdError, setThresholdError] = useState<string | undefined>();

  const inactive = useQuery({
    queryKey: ['admin', 'gdpr', 'inactive', threshold],
    queryFn: () => fetchInactiveUsers(threshold),
    placeholderData: keepPreviousData,
  });

  const userList = [...(users.data?.users ?? [])].sort((a, b) => a.email.localeCompare(b.email));
  const selected = userList.find((u) => u.id === userId) ?? null;

  async function onExport(): Promise<void> {
    if (selected === null) return;
    setExportError(false);
    setDone('');
    setExporting(true);
    try {
      const json = await exportUserData(selected.id);
      downloadJson(`reefguide-export-${selected.id}.json`, json);
      setDone(t('adminArea.gdpr.exported', { email: selected.email }));
    } catch {
      setExportError(true);
    } finally {
      setExporting(false);
    }
  }

  function askAnonymize(): void {
    if (selected === null) return;
    const target = selected;
    setDone('');
    setConfirm({
      title: t('adminArea.gdpr.anonymize'),
      message: t('adminArea.gdpr.confirmAnonymize', { email: target.email }),
      confirmLabel: t('adminArea.gdpr.anonymizeConfirm'),
      requireText: target.email,
      onConfirm: async () => {
        await anonymizeUser(target.id);
        setUserId('');
        setDone(t('adminArea.gdpr.anonymized', { email: target.email }));
        await queryClient.invalidateQueries({ queryKey: ['admin'] });
      },
    });
  }

  function onRetentionSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const days = Number.parseInt(thresholdInput, 10);
    if (!Number.isInteger(days) || days < 1) {
      setThresholdError(t('adminArea.gdpr.thresholdInvalid'));
      return;
    }
    setThresholdError(undefined);
    setThreshold(days);
  }

  const inactiveUsers = inactive.data?.users ?? [];

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="wide">
          <PageHeader title={t('adminArea.title')} description={t('adminArea.gdpr.description')} />
          <AdminArea>
            <Section title={t('adminArea.gdpr.subjectTitle')} description={t('adminArea.gdpr.subjectDescription')}>
              <LiveRegion>{done}</LiveRegion>
              {users.isLoading && (
                <LiveRegion>
                  <Text>{t('adminArea.users.loading')}</Text>
                </LiveRegion>
              )}
              {users.isError && (
                <LoadErrorState title={t('adminArea.users.loadError')} onRetry={() => void users.refetch()} />
              )}
              {!users.isLoading && !users.isError && userList.length === 0 && (
                <EmptyState
                  icon={<ShieldCheck size={48} strokeWidth={1.5} aria-hidden="true" />}
                  headline={t('adminArea.users.emptyTitle')}
                  description={t('adminArea.users.emptyMessage')}
                  actionLabel={t('adminArea.reload')}
                  onAction={() => void users.refetch()}
                  headingLevel={3}
                />
              )}
              {!users.isLoading && !users.isError && userList.length > 0 && (
                <Card padding="lg">
                  <Stack gap="4">
                    <Select
                      label={t('adminArea.gdpr.user')}
                      value={userId}
                      onChange={(e) => {
                        setUserId(e.target.value);
                        setExportError(false);
                      }}
                      options={[
                        { value: '', label: t('adminArea.gdpr.chooseUser') },
                        ...userList.map((u) => ({ value: u.id, label: u.email })),
                      ]}
                      fullWidth
                    />
                    <div className="rg-admin-table__actions">
                      <Button
                        variant="secondary"
                        onClick={() => void onExport()}
                        disabled={selected === null || exporting}
                        isLoading={exporting}
                      >
                        {t('adminArea.gdpr.export')}
                      </Button>
                      <Button variant="danger" onClick={askAnonymize} disabled={selected === null}>
                        {t('adminArea.gdpr.anonymize')}
                      </Button>
                    </div>
                    {exportError && <Alert compact>{t('adminArea.gdpr.exportFailed')}</Alert>}
                  </Stack>
                </Card>
              )}
            </Section>

            <Section title={t('adminArea.gdpr.retentionTitle')} description={t('adminArea.gdpr.retentionDescription')}>
              <Card padding="md">
                <form className="rg-admin-form" onSubmit={onRetentionSubmit} noValidate>
                  <Input
                    type="number"
                    label={t('adminArea.gdpr.threshold')}
                    value={thresholdInput}
                    min={1}
                    step={1}
                    inputMode="numeric"
                    onChange={(e) => setThresholdInput(e.target.value)}
                    error={thresholdError}
                    fullWidth
                  />
                  <div>
                    <Button type="submit" variant="secondary">
                      {t('adminArea.gdpr.check')}
                    </Button>
                  </div>
                </form>
              </Card>

              {inactive.isLoading && (
                <LiveRegion>
                  <Text>{t('adminArea.gdpr.loading')}</Text>
                </LiveRegion>
              )}
              {inactive.isError && (
                <LoadErrorState title={t('adminArea.gdpr.loadError')} onRetry={() => void inactive.refetch()} />
              )}
              {!inactive.isLoading && !inactive.isError && inactiveUsers.length === 0 && (
                <EmptyState
                  icon={<ShieldCheck size={48} strokeWidth={1.5} aria-hidden="true" />}
                  headline={t('adminArea.gdpr.emptyTitle')}
                  description={t('adminArea.gdpr.emptyMessage', { days: threshold })}
                  actionLabel={t('adminArea.reload')}
                  onAction={() => void inactive.refetch()}
                  headingLevel={3}
                />
              )}
              {!inactive.isLoading && !inactive.isError && inactiveUsers.length > 0 && (
                <AdminTable caption={t('adminArea.gdpr.tableCaption', { days: threshold })}>
                  <thead>
                    <tr>
                      <th scope="col">{t('adminArea.gdpr.col.email')}</th>
                      <th scope="col">{t('adminArea.gdpr.col.status')}</th>
                      <th scope="col">{t('adminArea.gdpr.col.lastLogin')}</th>
                      <th scope="col">{t('adminArea.gdpr.col.createdAt')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inactiveUsers.map((u) => (
                      <tr key={u.id}>
                        <th scope="row">{u.email}</th>
                        <td>
                          <Badge tone={u.status === 'active' ? 'success' : 'warning'}>
                            {String(t(`adminArea.users.status.${u.status}`, { defaultValue: u.status }))}
                          </Badge>
                        </td>
                        <td className="rg-admin-table__numeric">
                          {u.lastLoginAt ? formatDate(u.lastLoginAt, i18n.language) : t('adminArea.gdpr.neverLoggedIn')}
                        </td>
                        <td className="rg-admin-table__numeric">{formatDate(u.createdAt, i18n.language)}</td>
                      </tr>
                    ))}
                  </tbody>
                </AdminTable>
              )}
            </Section>

            <AdminConfirmDialog request={confirm} onClose={() => setConfirm(null)} />
          </AdminArea>
        </PageContent>
      }
    />
  );
}

export default AdminGdprPage;
